import { ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronUp, Download, Eye, Pencil, Plus, Search, Trash2, Upload } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  PermissionGuard,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useScopedPartnerNav } from "@/context/auth/scoped-partner-nav";
import { formatCpfCnpj } from "@/core/utils";

import { ClientStatus } from "../clients.types";
import { SortField, useSubPartners } from "./sub-partners.hooks";

export const SubPartnersPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { partnerHomePath } = useScopedPartnerNav(clientId);

  const {
    subPartners,
    filteredCount,
    totalCount,
    isLoading,
    search,
    setSearch,
    sortField,
    sortDir,
    toggleSort,
    filterStatus,
    setFilterStatus,
    filterType,
    setFilterType,
    page,
    pageSize,
    totalPages,
    goToPage,
    handlePageSizeChange,
    handleExportCsv,
    importMutation,
    pendingRowCount,
    handleImportFile,
    triggerImport,
    importFileRef,
    handleDelete,
  } = useSubPartners(clientId!);

  const getStatusBadge = (status: string | undefined) => {
    switch (status) {
      case ClientStatus.approved:
        return <Badge className="bg-green-500 text-white hover:bg-green-600">Aprovado</Badge>;
      case ClientStatus.pending_data:
        return <Badge className="bg-yellow-500 text-white hover:bg-yellow-600">Dados pendentes</Badge>;
      case ClientStatus.pending_review:
        return <Badge className="bg-blue-500 text-white hover:bg-blue-600">Em revisão</Badge>;
      case ClientStatus.rejected:
        return <Badge className="bg-red-500 text-white hover:bg-red-600">Rejeitado</Badge>;
      default:
        return <Badge variant="outline">{status || "—"}</Badge>;
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === "asc"
      ? <ChevronUp className="h-3 w-3 ml-1" />
      : <ChevronDown className="h-3 w-3 ml-1" />;
  };

  const startRow = filteredCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRow = Math.min(page * pageSize, filteredCount);

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(partnerHomePath)}
            className="mb-1 text-muted-foreground -ml-2"
          >
            ← Voltar
          </Button>
          <h1 className="text-2xl font-bold">Parceiros</h1>
          <p className="text-muted-foreground text-sm">
            {totalCount} parceiro(s) cadastrado(s) nesta organização
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleExportCsv}>
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={triggerImport}
            disabled={importMutation.isPending}
          >
            <Upload className="h-4 w-4 mr-2" />
            {importMutation.isPending ? "Importando..." : "Importar XLSX/CSV"}
          </Button>
          <input
            ref={importFileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleImportFile}
          />

          <Button size="sm" onClick={() => navigate(`/clients/${clientId}/sub-partners/new`)}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Parceiro
          </Button>
        </div>
      </div>

      {/* Barra de progresso de importação */}
      {importMutation.isPending && (
        <div className="rounded-lg border bg-muted/40 p-4 space-y-2">
          <div className="flex justify-between text-sm font-medium">
            <span>
              {pendingRowCount != null
                ? `Processando ${pendingRowCount} parceiros…`
                : "Processando importação…"}
            </span>
            <span className="text-muted-foreground text-xs">Aguarde</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div className="h-2 bg-primary rounded-full animate-[progress-indeterminate_1.5s_ease-in-out_infinite]" style={{ width: "40%" }} />
          </div>
          {pendingRowCount != null && (
            <p className="text-xs text-muted-foreground">
              Isso pode levar alguns segundos para {pendingRowCount} registros
            </p>
          )}
        </div>
      )}

      {/* Filtros */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, documento ou e-mail"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value={ClientStatus.approved}>Aprovado</SelectItem>
            <SelectItem value={ClientStatus.pending_data}>Dados pendentes</SelectItem>
            <SelectItem value={ClientStatus.pending_review}>Em revisão</SelectItem>
            <SelectItem value={ClientStatus.rejected}>Rejeitado</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterType} onValueChange={(v) => setFilterType(v as any)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">PJ e PF</SelectItem>
            <SelectItem value="pj">Pessoa Jurídica</SelectItem>
            <SelectItem value="pf">Pessoa Física</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader className="py-3 px-4 border-b flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {filteredCount !== totalCount
              ? `${filteredCount} resultado(s) filtrado(s) de ${totalCount}`
              : `${totalCount} parceiro(s)`}
          </CardTitle>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Linhas por página:</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => handlePageSizeChange(Number(v))}
            >
              <SelectTrigger className="h-7 w-16 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 50, 100].map((s) => (
                  <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer select-none hover:text-foreground"
                  onClick={() => toggleSort("name")}
                >
                  <span className="flex items-center">
                    Nome <SortIcon field="name" />
                  </span>
                </TableHead>
                <TableHead>Documento</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead
                  className="cursor-pointer select-none hover:text-foreground"
                  onClick={() => toggleSort("status")}
                >
                  <span className="flex items-center">
                    Status <SortIcon field="status" />
                  </span>
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none hover:text-foreground"
                  onClick={() => toggleSort("created_at")}
                >
                  <span className="flex items-center">
                    Cadastrado em <SortIcon field="created_at" />
                  </span>
                </TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Carregando…
                  </TableCell>
                </TableRow>
              ) : subPartners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    {search || filterStatus !== "all" || filterType !== "all"
                      ? "Nenhum parceiro encontrado com os filtros aplicados."
                      : 'Nenhum parceiro cadastrado. Clique em "+ Novo Parceiro" para começar.'}
                  </TableCell>
                </TableRow>
              ) : (
                subPartners.map((partner) => (
                  <TableRow key={partner.id}>
                    <TableCell className="font-medium">{partner.name || "—"}</TableCell>
                    <TableCell>
                      {partner.cnpj
                        ? formatCpfCnpj(partner.cnpj)
                        : (partner as any).cpf
                        ? formatCpfCnpj((partner as any).cpf)
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{partner.cnpj ? "PJ" : "PF"}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{partner.email || "—"}</TableCell>
                    <TableCell>{getStatusBadge(partner.status)}</TableCell>
                    <TableCell className="text-sm">
                      {partner.created_at
                        ? new Date(partner.created_at).toLocaleDateString("pt-BR")
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="Visualizar"
                          onClick={() =>
                            navigate(`/clients/${clientId}/sub-partners/${partner.id}`)
                          }
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="Editar"
                          onClick={() =>
                            navigate(`/clients/${clientId}/sub-partners/${partner.id}/edit`)
                          }
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          title="Excluir"
                          onClick={() => handleDelete(partner)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-muted-foreground">
            <span>
              {startRow}–{endRow} de {filteredCount}
            </span>

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => goToPage(1)}
                disabled={page === 1}
                title="Primeira página"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
                title="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {/* Números de página */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce<(number | "...")[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === "..." ? (
                    <span key={`ellipsis-${idx}`} className="px-1">…</span>
                  ) : (
                    <Button
                      key={item}
                      variant={page === item ? "default" : "ghost"}
                      size="icon"
                      className="h-7 w-7 text-xs"
                      onClick={() => goToPage(item as number)}
                    >
                      {item}
                    </Button>
                  )
                )}

              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => goToPage(page + 1)}
                disabled={page === totalPages}
                title="Próxima página"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => goToPage(totalPages)}
                disabled={page === totalPages}
                title="Última página"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
